import { memo, ReactNode } from 'react';

import {
    DataModelAttribute,
    DataModelAttributeBaseClass,
    DataModelAttributeTypeName,
} from '@/types/DataModel';

import {
    BooleanComponent,
    DateComponent,
    DateTimeComponent,
    DimensionalComponent,
    EnumComponent,
    FloatComponent,
    ImageComponent,
    IntegerComponent,
    PhysicalComponent,
    StringComponent,
} from '..';
import { useScrollCellIntoView } from '../hooks/useScrollCellIntoView';

interface AttributeGridCellProps {
    /**
     * The data model attribute being rendered. `attribute.item` and
     * `attribute.additionalParams.collectionRowPath` will be set here so
     * that `useOptions` and dependent selectors can access the current
     * row's values (mirrors what Collection's `Cell.tsx` was doing before
     * the extraction).
     */
    attribute: DataModelAttribute;
    /**
     * The RHF field path for the value bound to this cell. Consumers must
     * mount an enclosing `FormProvider`. For the item list this is just the
     * attribute name (flat row form). For collections it is
     * `${collectionAttributeFieldPath}.${rowIndex}.${attributeName}`.
     */
    fieldPath: string;
    /**
     * Optional row snapshot used to seed `attribute.item` so that dependent
     * option/filter code sees the current row. When omitted the caller
     * should set `attribute.item` itself before rendering.
     */
    rowData?: Record<string, unknown>;
    /**
     * Optional row-scoped path (e.g. `collection.0` or the primary key of a
     * list row). Attached to `attribute.additionalParams.collectionRowPath`
     * so `useOptions` can read reactive form values.
     */
    rowPath?: string;
    /**
     * Optional row index, used for stable className hooks (parity with the
     * legacy Collection cell CSS selectors) and as part of the child key.
     */
    rowIndex?: number;
    readonly?: boolean;
    className?: string;
    /** LAX-10059: ArrowUpTray upload icon, hit list / catalog only. */
    trayUpload?: boolean;
    /** Open the asset dialog as soon as an image/document editor mounts. */
    openUploadOnMount?: boolean;
    /** Open the calendar as soon as a date editor mounts. */
    openCalendarOnMount?: boolean;
    onUploadOpened?: () => void;
    /** Hit list: upload dialog closed without applying a file. */
    onUploadDismissed?: () => void;
    /** Hit list dimensional cells: language key chosen before edit mode. */
    activeDimension?: string;
    onActiveDimensionChange?: (key: string) => void;
}

/**
 * Shared per-type editor dispatch used by every grid that renders
 * editable attribute cells (Collection, MultiReference, MultiDimensional
 * and the browse items list).
 *
 * All branches render the same `<div>` shell (with `useScrollCellIntoView`
 * so a sticky column on the right does not obscure the active cell); only
 * the wrapped `*Component` changes.
 */
const CoreAttributeGridCell = ({
    attribute,
    fieldPath,
    rowData,
    rowPath,
    rowIndex,
    readonly,
    className,
    trayUpload = false,
    openUploadOnMount = false,
    openCalendarOnMount = false,
    onUploadOpened,
    onUploadDismissed,
    activeDimension,
    onActiveDimensionChange,
}: AttributeGridCellProps) => {
    const { cellRef, scrollCellIntoView } = useScrollCellIntoView();

    if (rowData !== undefined) {
        // Assign the row snapshot on the attribute so `useOptions` and other
        // selectors can access it (same trick Collection/Cell.tsx used).
        attribute.item = rowData;
    }
    if (rowPath !== undefined) {
        attribute.additionalParams = {
            ...attribute.additionalParams,
            collectionRowPath: rowPath,
        };
    }

    const indexSuffix = rowIndex !== undefined ? `_${rowIndex}` : '';
    const wrapperClass = `${attribute.name}${indexSuffix}-cell${className ? ` ${className}` : ''}`;
    const inputClass = `${attribute.name}${indexSuffix}-input-cell`;
    const componentKey = `${rowIndex ?? 'row'}-${attribute.name}`;

    const componentProps = {
        attribute,
        gridMode: true as const,
        hideLabel: true as const,
        fieldPath,
        className: inputClass,
        readonly,
    };

    let editor: ReactNode;
    switch (attribute.typeName || attribute.baseClass) {
        case DataModelAttributeTypeName.Boolean:
            editor = <BooleanComponent key={componentKey} {...componentProps} />;
            break;
        case DataModelAttributeTypeName.Date:
            editor = (
                <DateComponent
                    key={componentKey}
                    {...componentProps}
                    openCalendarOnMount={openCalendarOnMount}
                />
            );
            break;
        case DataModelAttributeTypeName.DateTime:
            editor = (
                <DateTimeComponent
                    key={componentKey}
                    {...componentProps}
                    openCalendarOnMount={openCalendarOnMount}
                />
            );
            break;
        case DataModelAttributeTypeName.Enum:
            editor = <EnumComponent key={componentKey} {...componentProps} />;
            break;
        case DataModelAttributeBaseClass.Float:
            editor = <FloatComponent key={componentKey} {...componentProps} />;
            break;
        case DataModelAttributeTypeName.Image:
            editor = (
                <ImageComponent
                    key={componentKey}
                    {...componentProps}
                    trayUpload={trayUpload}
                    openSelectOnMount={openUploadOnMount}
                    onSelectOpened={onUploadOpened}
                    onSelectDismissed={onUploadDismissed}
                />
            );
            break;
        case DataModelAttributeTypeName.Document:
            editor = (
                <ImageComponent
                    key={componentKey}
                    {...componentProps}
                    mode="Document"
                    trayUpload={trayUpload}
                    openSelectOnMount={openUploadOnMount}
                    onSelectOpened={onUploadOpened}
                    onSelectDismissed={onUploadDismissed}
                />
            );
            break;
        case DataModelAttributeBaseClass.Integer:
            editor = <IntegerComponent key={componentKey} {...componentProps} />;
            break;
        case DataModelAttributeTypeName.Physical:
            editor = <PhysicalComponent key={componentKey} {...componentProps} />;
            break;
        case DataModelAttributeTypeName.Dimensional:
        case DataModelAttributeTypeName.MultiLineDimensional:
            editor = (
                <DimensionalComponent
                    key={componentKey}
                    {...componentProps}
                    activeDimension={activeDimension}
                    onActiveDimensionChange={onActiveDimensionChange}
                />
            );
            break;
        default:
            editor = <StringComponent key={componentKey} {...componentProps} />;
            break;
    }

    return (
        <div
            ref={cellRef}
            className={trayUpload
                ? `${wrapperClass} tw-flex tw-h-full tw-w-full tw-min-w-0 tw-items-center tw-justify-center`
                : wrapperClass}
            onFocusCapture={scrollCellIntoView}
            onClick={scrollCellIntoView}
        >
            {editor}
        </div>
    );
};

const AttributeGridCell = memo(CoreAttributeGridCell);

export { AttributeGridCell };
